import { mdiPauseCircleOutline, mdiGavel, mdiAlertCircleOutline, mdiCodeTags } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useEffect, useMemo, useState } from 'react';
import { CoreFetch } from '../../../api';
import { CodingApi } from '../../../api/server';
import { ProviderCoding, SupportCoding } from '../../../api/server/models';
import { formatDate } from '../../../services/primitives/date-utils';
import { Variables } from '../../base';
import { parseCodingNotes } from '../../claim-details/claim-timeline/items/coding-detail.utils';
import { IParsedCommentReason, parseCommentReason } from './comment-reason.utils';
import * as S from './coding-reason-card.styles';

export interface ICodingReasonCardsProps {
    codingIds: string[];
    isSupportApp: boolean;
    subject?: string;
    categoryNames?: string[];
    /** Earliest comment body — used when there is no linked coding. */
    commentBody?: string;
    /** Message created timestamp for comment-based cards. */
    messageCreated?: string;
}

type CodingRecord = (ProviderCoding | SupportCoding) & { id: string };

export function getCodingSectionTitle(subject?: string, categoryNames: string[] = []): string {
    const haystack = [subject, ...categoryNames]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    if (/halt/.test(haystack)) {
        return "Why it's halted";
    }
    if (/coding problem|creation problem|validation/.test(haystack)) {
        return /coding problem/.test(haystack) ? "Why it's halted" : "Why it couldn't be created";
    }
    if (/denial|denied/.test(haystack)) {
        return 'Denial reason';
    }
    if (/reject/.test(haystack)) {
        return 'Rejection reason';
    }
    return 'Rules applied';
}

function getSectionIcon(subject?: string, categoryNames: string[] = []): string {
    const haystack = [subject, ...categoryNames]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    if (/halt|coding problem/.test(haystack)) {
        return mdiPauseCircleOutline;
    }
    if (/creation problem|validation/.test(haystack)) {
        return mdiAlertCircleOutline;
    }
    if (/denial|denied/.test(haystack)) {
        return mdiGavel;
    }
    if (/reject/.test(haystack)) {
        return mdiAlertCircleOutline;
    }
    return mdiCodeTags;
}

function formatRulesAppliedLabel(count: number): string {
    if (count === 1) {
        return '1 rule was applied during this coding pass.';
    }
    return `${count} rules were applied during this coding pass.`;
}

function commentCardTitle(parsed: IParsedCommentReason): string {
    if (parsed.title) {
        return parsed.title;
    }
    const firstLine = parsed.headline.split(/\r?\n/).map(l => l.trim()).find(Boolean);
    if (!firstLine) {
        return 'Validation error';
    }
    if (firstLine.length <= 80) {
        return firstLine;
    }
    return `${firstLine.slice(0, 77)}…`;
}

function formatPayloadForDisplay(parsed: IParsedCommentReason): string {
    if (parsed.payloadJson !== undefined) {
        try {
            return JSON.stringify(parsed.payloadJson, null, 2);
        } catch {
            // fall through
        }
    }
    return parsed.payloadRaw || '';
}

const CommentReasonCard: React.FunctionComponent<{
    parsed: IParsedCommentReason;
    subject?: string;
    categoryNames: string[];
    messageCreated?: string;
}> = ({ parsed, subject, categoryNames, messageCreated }) => {
    const sectionIcon = getSectionIcon(subject, [...categoryNames, parsed.title, parsed.kind].filter(Boolean) as string[]);
    const iconColor = Variables.ThemeLight.AttentionConstant6;
    const haltedOn = messageCreated ? formatDate(messageCreated, 'MMM d, yyyy') : '—';
    const raw = formatPayloadForDisplay(parsed);
    const problems = parsed.problems || [];
    const hasProblems = problems.length > 0;
    const title = hasProblems ? commentCardTitle(parsed) : 'Validation errors';

    return (
        <S.Card>
            <S.CardHeader>
                <Icon path={sectionIcon} size={0.75} color={iconColor} />
                <S.CardTitle>{title}</S.CardTitle>
            </S.CardHeader>

            {hasProblems ? (
                <S.Explain>
                    <S.ExplainLabel>{parsed.explainLabel || 'Error'}</S.ExplainLabel>
                    {parsed.explain || parsed.headline}
                </S.Explain>
            ) : null}

            {hasProblems ? (
                <S.ProblemsBlock>
                    <S.ProblemsHeading>
                        What needs to be fixed · {problems.length}
                    </S.ProblemsHeading>
                    <S.ProblemsList>
                        {problems.map((problem, i) => (
                            <S.ProblemRow key={`${problem.slot}-${problem.value || problem.why}-${i}`}>
                                <S.ProblemIndex className="tabular">{i + 1}</S.ProblemIndex>
                                <S.ProblemBody>
                                    <S.ProblemTitleRow>
                                        <S.ProblemSlot>{problem.slot}</S.ProblemSlot>
                                        {problem.who ? <S.ProblemWho>{problem.who}</S.ProblemWho> : null}
                                    </S.ProblemTitleRow>
                                    <S.ProblemDetailRow>
                                        {problem.value ? (
                                            <S.ProblemValue className="tabular">{problem.value}</S.ProblemValue>
                                        ) : null}
                                        <S.ProblemWhy>{problem.why}</S.ProblemWhy>
                                    </S.ProblemDetailRow>
                                </S.ProblemBody>
                            </S.ProblemRow>
                        ))}
                    </S.ProblemsList>
                </S.ProblemsBlock>
            ) : null}

            {raw ? (
                hasProblems ? (
                    <S.RawBlock>
                        <S.RawDetails>
                            <S.RawSummary>{parsed.rawLabel || 'Raw response'}</S.RawSummary>
                            <S.RawPre>{raw}</S.RawPre>
                        </S.RawDetails>
                    </S.RawBlock>
                ) : (
                    <S.RawBlock>
                        <S.RawPre style={{ marginTop: 0 }}>{raw}</S.RawPre>
                    </S.RawBlock>
                )
            ) : null}

            <S.MetaFooter>
                <S.MetaCell>
                    <S.MetaLabel>{parsed.missingLabel ? 'Missing' : 'Source'}</S.MetaLabel>
                    <S.MetaValue>
                        {parsed.missingLabel || 'Opening comment'}
                    </S.MetaValue>
                </S.MetaCell>
                <S.MetaCell $borderLeft>
                    <S.MetaLabel>Halted</S.MetaLabel>
                    <S.MetaValue className="tabular">{haltedOn}</S.MetaValue>
                </S.MetaCell>
            </S.MetaFooter>
        </S.Card>
    );
};

export const CodingReasonCards: React.FunctionComponent<ICodingReasonCardsProps> = ({
    codingIds,
    isSupportApp,
    subject,
    categoryNames = [],
    commentBody,
    messageCreated,
}) => {
    const [codings, setCodings] = useState<CodingRecord[]>([]);
    const [loading, setLoading] = useState(false);

    const codingIdsKey = useMemo(
        () => (codingIds?.length ? [...codingIds].sort().join(',') : ''),
        [codingIds],
    );

    const parsedCommentReason = useMemo(() => parseCommentReason(commentBody), [commentBody]);

    useEffect(() => {
        let cancelled = false;

        const fetchCodings = async () => {
            if (!codingIds || codingIds.length === 0) {
                setCodings([]);
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const codingApi = CoreFetch(CodingApi);
                const results = await Promise.all(
                    codingIds.map(async id => {
                        try {
                            let coding: ProviderCoding | SupportCoding;
                            if (isSupportApp) {
                                coding = await codingApi.getSupportCoding({ id });
                            } else {
                                coding = await codingApi.getProviderCoding({ id });
                            }
                            return { ...coding, id: coding.id || id } as CodingRecord;
                        } catch (error) {
                            console.error(`Failed to fetch coding ${id}:`, error);
                            return { id, notes: undefined } as CodingRecord;
                        }
                    }),
                );
                if (!cancelled) {
                    setCodings(results);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchCodings();
        return () => {
            cancelled = true;
        };
    }, [codingIdsKey, isSupportApp]);

    const sectionTitle = getCodingSectionTitle(subject, categoryNames);
    const sectionIcon = getSectionIcon(subject, categoryNames);
    const iconColor = /halt|reject|denial|denied|coding problem|creation problem|validation/i.test(
        [subject, ...categoryNames].join(' '),
    )
        ? Variables.ThemeLight.AttentionConstant6
        : Variables.ThemeLight.TextMedium;

    const hasCodingIds = !!(codingIds && codingIds.length > 0);

    // Prefer linked coding records; fall back to parsed opening-comment reason.
    if (!hasCodingIds) {
        if (!parsedCommentReason) {
            return null;
        }
        return (
            <S.Section>
                <S.SectionLabel>{sectionTitle}</S.SectionLabel>
                <S.Stack>
                    <CommentReasonCard
                        parsed={parsedCommentReason}
                        subject={subject}
                        categoryNames={categoryNames}
                        messageCreated={messageCreated}
                    />
                </S.Stack>
            </S.Section>
        );
    }

    if (loading) {
        return (
            <S.Section>
                <S.SectionLabel>{sectionTitle}</S.SectionLabel>
                <S.LoadingState>Loading coding details...</S.LoadingState>
            </S.Section>
        );
    }

    return (
        <S.Section>
            <S.SectionLabel>{sectionTitle}</S.SectionLabel>
            <S.Stack>
                {codings.map(coding => {
                    const rules = parseCodingNotes(coding.notes);
                    const haltedOn = coding.created
                        ? formatDate(coding.created, 'MMM d yyyy')
                        : '—';

                    return (
                        <S.Card key={coding.id}>
                            <S.CardHeader>
                                <Icon path={sectionIcon} size={0.75} color={iconColor} />
                                <S.CardTitle>{formatRulesAppliedLabel(rules.length)}</S.CardTitle>
                            </S.CardHeader>

                            {rules.length > 0 ? (
                                <S.RulesList>
                                    {rules.map(rule => (
                                        <S.RuleRow key={`${coding.id}-${rule.n}-${rule.text}`}>
                                            <S.RuleBadge className="tabular">Rule {rule.n}</S.RuleBadge>
                                            <S.RuleText>{rule.text}</S.RuleText>
                                        </S.RuleRow>
                                    ))}
                                </S.RulesList>
                            ) : (
                                <S.EmptyState>
                                    {coding.notes
                                        ? String(coding.notes)
                                        : 'No rules recorded for this coding pass.'}
                                </S.EmptyState>
                            )}

                            <S.MetaFooter>
                                <S.MetaCell>
                                    <S.MetaLabel>Vendor</S.MetaLabel>
                                    <S.MetaValue>{coding.vendor || '—'}</S.MetaValue>
                                </S.MetaCell>
                                <S.MetaCell $borderLeft>
                                    <S.MetaLabel>Halted</S.MetaLabel>
                                    <S.MetaValue className="tabular">{haltedOn}</S.MetaValue>
                                </S.MetaCell>
                            </S.MetaFooter>
                        </S.Card>
                    );
                })}
            </S.Stack>
        </S.Section>
    );
};
